import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  IApplicationPerformanceKpis,
  IApplicationPerformanceKpisPerUser,
  IApplicationPerformanceKpisPerUserGroup,
  IEventAnalyticsQueryParams,
} from './admin-event-analytics.interface';

const MOCK_REQUEST_DELAY_MS = 350;
const MOCK_GROUPS_PER_USER_PAGE = 3;
// Matches `BASELINE_KPIS.activeUsers` - large enough that the Users tab's stacked bar/circular
// graph "top 5" framing and the breakdown table's pagination are actually meaningful, instead
// of trivially showing "all of them".
const MOCK_USER_COUNT = 93;

/** Roughly matches a healthy mid-size deployment - individual fields are then scaled per request (see `buildMockKpis`). */
const BASELINE_KPIS: IApplicationPerformanceKpis = {
  activeCampaigns: 314,
  activeUserGroups: 5,
  activeUsers: 93,
  activeUsersAvgDaily: 34,
  activeUsersFrontend: 88,
  activeUsersMcp: 21,
  avgBriefingsPerCampaign: 0.45,
  avgMcpActiveDaysPerUser: 3.4,
  avgMcpCallDuration: 850,
  avgMcpToolCallsPerSession: 6.2,
  avgReportsPerCampaign: 0.4,
  avgTimeInAnalyticsPopupDeliverableView: 56_000,
  avgTimeInAnalyticsPopupOverview: 75_000,
  avgTimeToCampaignCreationFinished: 452_000,
  avgViewsPerBriefing: 2.1,
  avgViewsPerReport: 2.4,
  briefingAdaptionConversion: 0.097,
  briefingOpenRate: 1,
  briefingsCreated: 512,
  campaignCompletionRate: 0.769,
  campaignCreationsFinished: 1359,
  campaignCreationsStarted: 1800,
  campaignsWithAnalyticsViewed: 0.71,
  campaignsWithAnalyticsViewedMcp: 0.18,
  campaignsWithBriefings: 480,
  campaignsWithReports: 610,
  mcpAdoptionRate: 0.226,
  mcpConnectedUsers: 9,
  mcpSessions: 140,
  mcpToolCalls: 868,
  mcpToolErrorRate: 0.012,
  mcpToolRejectedRate: 0.034,
  reportAdaptionConversion: 0.663,
  reportOpenRate: 1,
  reportsCreated: 404,
  timeToFirstAnalyticsPopupOpened: 95_000,
  timeToFirstBriefingCreated: 540_000,
  timeToFirstCampaignCreationFinished: 620_000,
  timeToFirstMcpToolCall: 1_450_000,
  timeToFirstReportPublished: 610_000,
  totalUserGroups: 7,
  totalUsers: 140,

  // Costs (mock, in USD)
  avgCampaignCosts: 42.5,
  avgDailyCosts: 620,
  globalCosts: 18_500,
  highestCampaignCosts: 310,
};

/**
 * No backend endpoint exists for these yet - these three methods are named the way the real
 * requests will eventually be (so swapping in a real `HttpClient` call later is a one-line
 * change per method) but currently resolve to deterministic mock data derived from the query
 * params, so the UI has believable, filter-reactive numbers to work against in the meantime.
 */
@Injectable({ providedIn: 'root' })
export class AdminEventAnalyticsService {
  public getApplicationPerformanceKpis(params: IEventAnalyticsQueryParams): Observable<IApplicationPerformanceKpis> {
    return of(this.buildMockKpis(this.buildSeed(params))).pipe(delay(MOCK_REQUEST_DELAY_MS));
  }

  public getApplicationPerformanceKpisPerUserGroup(
    params: IEventAnalyticsQueryParams,
  ): Observable<IApplicationPerformanceKpisPerUserGroup[]> {
    const userGroupIds = params.userGroupIds.length ? params.userGroupIds : this.fallbackMockIds('group', 6);
    const baseSeed = this.buildSeed(params);

    return of(
      userGroupIds.map((userGroupId, index) => ({
        kpis: this.buildMockKpis(baseSeed + index + 1),
        userGroupId,
        userGroupName: this.mockGroupName(index),
      })),
    ).pipe(delay(MOCK_REQUEST_DELAY_MS));
  }

  public getApplicationPerformanceKpisPerUser(
    params: IEventAnalyticsQueryParams,
  ): Observable<IApplicationPerformanceKpisPerUser[]> {
    const userIds = this.fallbackMockIds('user', MOCK_USER_COUNT);
    const baseSeed = this.buildSeed(params);

    return of(
      userIds.map((userId, index) => {
        // Spread users across a handful of mock groups (rather than a real lookup) - there's no
        // backend relationship to reflect yet, so any deterministic assignment is as valid as another.
        const groupIndex = index % MOCK_GROUPS_PER_USER_PAGE;

        return {
          kpis: this.buildMockKpis(baseSeed + index + 101),
          userGroupId: this.fallbackMockIds('group', MOCK_GROUPS_PER_USER_PAGE)[groupIndex],
          userGroupName: this.mockGroupName(groupIndex),
          userId,
          userName: `Mock User ${index + 1}`,
        };
      }),
    ).pipe(delay(MOCK_REQUEST_DELAY_MS));
  }

  private mockGroupName(index: number): string {
    return `Mock Group ${index + 1}`;
  }

  private fallbackMockIds(prefix: string, count: number): string[] {
    return Array.from({ length: count }, (_unused, index) => `mock-${prefix}-${index + 1}`);
  }

  /** Deterministic seed from the query params, so identical filters always resolve to identical mock numbers. */
  private buildSeed(params: IEventAnalyticsQueryParams): number {
    const key = JSON.stringify({
      endDate: params.endDate?.getTime() ?? null,
      startDate: params.startDate?.getTime() ?? null,
      userGroupIds: [...params.userGroupIds].sort(),
    });

    let hash = 0;

    for (let i = 0; i < key.length; i++) {
      hash = (Math.imul(31, hash) + key.charCodeAt(i)) | 0;
    }

    return hash >>> 0;
  }

  private buildMockKpis(seed: number): IApplicationPerformanceKpis {
    const random = this.createSeededRandom(seed);
    const scale = (value: number): number => value * (0.82 + random() * 0.36);
    const scaleInt = (value: number): number => Math.max(0, Math.round(scale(value)));
    const scaleFraction = (value: number): number => Math.min(1, Math.max(0, scale(value)));

    return {
      activeCampaigns: scaleInt(BASELINE_KPIS.activeCampaigns),
      activeUserGroups: scaleInt(BASELINE_KPIS.activeUserGroups),
      activeUsers: scaleInt(BASELINE_KPIS.activeUsers),
      activeUsersAvgDaily: scaleInt(BASELINE_KPIS.activeUsersAvgDaily),
      activeUsersFrontend: scaleInt(BASELINE_KPIS.activeUsersFrontend),
      activeUsersMcp: scaleInt(BASELINE_KPIS.activeUsersMcp),
      avgBriefingsPerCampaign: scale(BASELINE_KPIS.avgBriefingsPerCampaign),
      avgCampaignCosts: scale(BASELINE_KPIS.avgCampaignCosts),
      avgDailyCosts: scale(BASELINE_KPIS.avgDailyCosts),
      avgMcpActiveDaysPerUser: scale(BASELINE_KPIS.avgMcpActiveDaysPerUser),
      avgMcpCallDuration: scaleInt(BASELINE_KPIS.avgMcpCallDuration),
      avgMcpToolCallsPerSession: scale(BASELINE_KPIS.avgMcpToolCallsPerSession),
      avgReportsPerCampaign: scale(BASELINE_KPIS.avgReportsPerCampaign),
      avgTimeInAnalyticsPopupDeliverableView: scaleInt(BASELINE_KPIS.avgTimeInAnalyticsPopupDeliverableView),
      avgTimeInAnalyticsPopupOverview: scaleInt(BASELINE_KPIS.avgTimeInAnalyticsPopupOverview),
      avgTimeToCampaignCreationFinished: scaleInt(BASELINE_KPIS.avgTimeToCampaignCreationFinished),
      avgViewsPerBriefing: scale(BASELINE_KPIS.avgViewsPerBriefing),
      avgViewsPerReport: scale(BASELINE_KPIS.avgViewsPerReport),
      briefingAdaptionConversion: scaleFraction(BASELINE_KPIS.briefingAdaptionConversion),
      briefingOpenRate: scaleFraction(BASELINE_KPIS.briefingOpenRate),
      briefingsCreated: scaleInt(BASELINE_KPIS.briefingsCreated),
      campaignCompletionRate: scaleFraction(BASELINE_KPIS.campaignCompletionRate),
      campaignCreationsFinished: scaleInt(BASELINE_KPIS.campaignCreationsFinished),
      campaignCreationsStarted: scaleInt(BASELINE_KPIS.campaignCreationsStarted),
      campaignsWithAnalyticsViewed: scaleFraction(BASELINE_KPIS.campaignsWithAnalyticsViewed),
      campaignsWithAnalyticsViewedMcp: scaleFraction(BASELINE_KPIS.campaignsWithAnalyticsViewedMcp),
      campaignsWithBriefings: scaleInt(BASELINE_KPIS.campaignsWithBriefings),
      campaignsWithReports: scaleInt(BASELINE_KPIS.campaignsWithReports),
      globalCosts: scale(BASELINE_KPIS.globalCosts),
      highestCampaignCosts: scale(BASELINE_KPIS.highestCampaignCosts),
      mcpAdoptionRate: scaleFraction(BASELINE_KPIS.mcpAdoptionRate),
      mcpConnectedUsers: scaleInt(BASELINE_KPIS.mcpConnectedUsers),
      mcpSessions: scaleInt(BASELINE_KPIS.mcpSessions),
      mcpToolCalls: scaleInt(BASELINE_KPIS.mcpToolCalls),
      mcpToolErrorRate: scaleFraction(BASELINE_KPIS.mcpToolErrorRate),
      mcpToolRejectedRate: scaleFraction(BASELINE_KPIS.mcpToolRejectedRate),
      reportAdaptionConversion: scaleFraction(BASELINE_KPIS.reportAdaptionConversion),
      reportOpenRate: scaleFraction(BASELINE_KPIS.reportOpenRate),
      reportsCreated: scaleInt(BASELINE_KPIS.reportsCreated),
      timeToFirstAnalyticsPopupOpened: scaleInt(BASELINE_KPIS.timeToFirstAnalyticsPopupOpened),
      timeToFirstBriefingCreated: scaleInt(BASELINE_KPIS.timeToFirstBriefingCreated),
      timeToFirstCampaignCreationFinished: scaleInt(BASELINE_KPIS.timeToFirstCampaignCreationFinished),
      timeToFirstMcpToolCall: scaleInt(BASELINE_KPIS.timeToFirstMcpToolCall),
      timeToFirstReportPublished: scaleInt(BASELINE_KPIS.timeToFirstReportPublished),
      totalUserGroups: scaleInt(BASELINE_KPIS.totalUserGroups),
      totalUsers: scaleInt(BASELINE_KPIS.totalUsers),
    };
  }

  /** Small, dependency-free PRNG (mulberry32) - deterministic per seed, good enough for mock data. */
  private createSeededRandom(seed: number): () => number {
    let state = seed;

    return (): number => {
      state += 0x6d2b79f5;

      let result = Math.imul(state ^ (state >>> 15), state | 1);

      result ^= result + Math.imul(result ^ (result >>> 7), result | 61);

      return ((result ^ (result >>> 14)) >>> 0) / 4_294_967_296;
    };
  }
}
